import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Filter as FilterIcon } from 'lucide-react';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { ActiveStatusPill, SystemPill } from '../../../shared/Pill';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import EmailTemplateDrawer from './EmailTemplateDrawer';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';

const FILTER_SCREEN_KEY = 'global-email-templates';
const DEFAULT_FILTERS = { status: 'all' };
const FIELD_FILTER_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_FILTER_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Global > Operations > Email Templates (story #236) — the admin-authored library
// vis_email_templates.md's send/compose flow (a separate future story)
// picks from. Default filter shows active and inactive together, greyed
// out when inactive, same as before — the Filters drawer/SavedFilterTabs
// (same shape as Field Help Settings) just let an admin optionally narrow
// to one or the other, or save that as a quick-access tab.
function EmailTemplateTable() {
  const { canCreate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { status: statusFilter } = filters;
  const applyFilters = (values) => setFilters(values);
  // Ad hoc "Filters" panel — draft is null while closed, so opening always
  // starts from the currently-applied filters rather than whatever was left
  // over from the last time it was open (same convention as FieldHelpTable).
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  const fetchRows = () => {
    setLoading(true);
    axios.get('/api/email-templates', { params: { search, status: statusFilter === 'all' ? undefined : statusFilter } })
      .then((res) => setRows(res.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, statusFilter]);

  const openAdd = () => { setEditingTemplate(null); setDrawerOpen(true); };
  const openEdit = (row) => { setEditingTemplate(row); setDrawerOpen(true); };

  const handleSaved = (row) => {
    setRows((prev) => {
      const exists = prev.some((r) => r.id === row.id);
      const next = exists ? prev.map((r) => (r.id === row.id ? row : r)) : [...prev, row];
      // A status-filtered view can now be hiding the row it just
      // saved/deactivated/reactivated — drop it locally rather than
      // waiting on a refetch to notice it no longer matches.
      const stillMatches = statusFilter === 'all' || (statusFilter === 'active') === !!row.is_active;
      return next.filter((r) => r.id !== row.id || stillMatches).sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const toggleActive = async (row) => {
    try {
      const res = await axios.patch(`/api/email-templates/${row.id}/${row.is_active ? 'deactivate' : 'reactivate'}`);
      handleSaved(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, `${row.is_active ? 'Deactivate' : 'Reactivate'} failed`);
    }
  };

  const renderDrawerFields = (draft, setDraftField) => (
    <div>
      <label style={FIELD_FILTER_LABEL_STYLE}>Status</label><br />
      <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
        <option value="all">Active & Inactive</option>
        <option value="active">Active only</option>
        <option value="inactive">Inactive only</option>
      </select>
    </div>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Email Templates</h2>
        {canCreate && (
          <button
            type="button"
            onClick={openAdd}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
          >
            <Plus size={15} strokeWidth={2.5} />
            Add Template
          </button>
        )}
      </div>
      <div style={{ margin: '8px 24px 0', fontSize: 13, color: '#5a6b85' }}>
        Reusable email templates (name, subject, body with merge fields) staff pick from when emailing a Commercial Entity. Click a row to edit.
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '16px 24px 0', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', border: '1px solid #d0d5dd', borderRadius: 8, maxWidth: 280 }}>
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '14px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Email Templates list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '14px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '42%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Subject</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No templates match.</td></tr>
            ) : rows.map((row) => (
              <tr
                key={row.id}
                className="list-row"
                onClick={() => openEdit(row)}
                style={{ cursor: 'pointer', opacity: row.is_active ? 1 : 0.55 }}
                title="Click to edit"
              >
                <td style={{ padding: '8px', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</td>
                <td style={{ padding: '8px', fontSize: 13, color: '#5a6b85', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.subject}>{row.subject}</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  {row.system_key
                    ? <SystemPill title="System Use — click to edit; required by Optim's core functionality." />
                    : <ActiveStatusPill active={row.is_active} />}
                </td>
                <td style={{ padding: '8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', onClick: () => openEdit(row) },
                      // A system template (issue #241) is never deactivated.
                      ...(row.system_key ? [] : [row.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, onClick: () => toggleActive(row) }
                        : { key: 'reactivate', label: 'Reactivate', onClick: () => toggleActive(row) }]),
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EmailTemplateDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        editingTemplate={editingTemplate}
        onSaved={handleSaved}
      />
    </div>
  );
}

export default EmailTemplateTable;
